// En: src/services/QuoteService.ts

import { IQuote } from "../types/Quotes";
import { apiClient } from "./apiClient";

/**
 * [READ] Obtiene todas las cotizaciones.
 */
export const getQuotes = async (): Promise<IQuote[]> => {
    try {
        return await apiClient('quotes', { method: 'GET' });
    } catch (error) {
        console.error("Error al obtener cotizaciones:", error);
        throw error;
    }
};

/**
 * [READ ONE] Obtiene una cotización por su ID.
 */
export const getQuoteById = async (id: number): Promise<IQuote | undefined> => {
    try {
        return await apiClient(`quotes/${id}`, { method: 'GET' });
    } catch (error) {
        console.error("Error al obtener cotización por ID:", error);
        return undefined;
    }
};

/**
 * [CREATE] Crea una nueva cotización.
 */
export const createQuote = async (quoteData: Omit<IQuote, 'id'>): Promise<IQuote> => {
    try {
        return await apiClient('quotes', {
            method: 'POST',
            body: JSON.stringify(quoteData),
        });
    } catch (error) {
        console.error("Error al crear cotización:", error);
        throw error;
    }
};

/**
 * [UPDATE] Actualiza una cotización existente.
 */
export const updateQuote = async (quoteData: IQuote): Promise<IQuote | undefined> => {
    try {
        const { id, ...updateData } = quoteData as any;
        delete updateData.deletedAt;

        return await apiClient(`quotes/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(updateData),
        });
    } catch (error) {
        console.error("Error al actualizar la cotización:", error);
        throw error;
    }
};

/**
 * [DELETE] Elimina una cotización.
 */
export const deleteQuote = async (id: number): Promise<boolean> => {
    try {
        await apiClient(`quotes/${id}`, { method: 'DELETE' });
        return true;
    } catch (error) {
        console.error("Error al eliminar cotización:", error);
        return false;
    }
};

// --- Funciones de Toggle ---

export const toggleQuoteReadStatus = async (id: number, isRead: boolean): Promise<IQuote | undefined> => {
    try {
        return await apiClient(`quotes/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ isRead }),
        });
    } catch (error) {
        console.error("Error al cambiar estado de lectura:", error);
        throw error;
    }
};

export const toggleQuoteArchived = async (id: number, isArchived: boolean): Promise<IQuote | undefined> => {
    try {
        return await apiClient(`quotes/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ isArchived }),
        });
    } catch (error) {
        console.error("Error al cambiar estado de archivado:", error);
        throw error;
    }
};

// --- Funciones Específicas para Cotizaciones ---

/**
 * Obtiene cotizaciones recientes (no archivadas)
 */
export const getRecentQuotes = async (limit: number = 5): Promise<IQuote[]> => {
    try {
        return await apiClient(`quotes/recent?limit=${limit}`, { method: 'GET' });
    } catch (error) {
        console.error("Error al obtener cotizaciones recientes:", error);
        throw error;
    }
};

/**
 * Obtiene el conteo de cotizaciones no leídas
 */
export const getUnreadCount = async (): Promise<number> => {
    try {
        return await apiClient('quotes/unread-count', { method: 'GET' });
    } catch (error) {
        console.error("Error al obtener conteo de no leídos:", error);
        return 0;
    }
};